import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { OneOnOnesEmployeesDocument, OneOnOnesMeetingsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { AiChat } from 'modules/meetings/components/AiChat'
import { CreateOneOnOneModal } from 'modules/meetings/components/CreateOneOnOneModal'
import { PageSkeleton } from 'modules/meetings/components/Skeletons/PageSkeleton'
import upsellOneOnOne from 'modules/meetings/lib/assets/upsellOneOnOne.png'
import { useOneOnOnesContext } from 'modules/meetings/lib/hooks/useOneOnOnesContext'
import {
  createMeetingInfo,
  getNextClosestMeeting,
  sortEmployeesByMeetingPriority,
} from 'modules/meetings/lib/utils/teamTableSorts'
import { useAccessCalendarSync } from 'modules/personal_settings/components/Settings/ExternalCalendar/useAccessCalendarSync'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

import { ConnectCalendarBanner } from './components/ConnectCalendarBanner'
import { TeamTable } from './components/TeamTable'

const OneOnOnesOverviewPage = () => {
  const {
    access: { id, locale },
    employee: currentEmployee,
  } = useCurrentSession()
  const { canSeeAdvancedMeetings } = useOneOnOnesContext()
  const [openCreateScheduledMeeting, setOpenCreateScheduledMeeting] = useState(false)
  const [aiChatOpen, setAiChatOpen] = useState(false)
  const [showAiChat, setShowAiChat] = useState(false)
  const { accessCalendarSync, loading } = useAccessCalendarSync(id)

  const { data, loading: loadingEmployees } = useQuery(OneOnOnesEmployeesDocument, {
    variables: {
      employeeId: currentEmployee.id,
      endDate: moment().add(3, 'months').format('YYYY-MM-DD'),
      startDate: moment().subtract(3, 'months').format('YYYY-MM-DD'),
    },
    skip: !currentEmployee.id,
  })

  const { data: lastMeetingData, loading: loadingLastMeeting } = useQuery(
    OneOnOnesMeetingsDocument,
    {
      variables: {
        employeeIds: data?.directReports?.employees?.map((employee) => employee.id) ?? [],
      },
      skip: !((data?.directReports?.employees?.length ?? 0) > 0),
    }
  )

  const initialEmployees = useMemo(() => {
    if (!data) return []

    const directReportsList = data?.directReports?.employees || []
    const myReporteesList = data?.myReportees?.employees?.map((employee) => employee.id) || []
    const historicalEmployeesList =
      data?.meetings?.attendees
        ?.map((node) => node.employee)
        .filter(
          (employee): employee is NonNullable<typeof employee> =>
            employee != null && employee.id != null
        ) || []

    // Extract employees from scheduled meetings
    const scheduledMeetingsEmployeesList =
      data?.meetings?.scheduledMeetingsConnection?.nodes?.flatMap(
        (meeting) =>
          meeting.template?.templateAttendees
            ?.map((attendee) => attendee.employee)
            .filter(Boolean) || []
      ) || []

    const uniqueEmployees = [
      ...directReportsList,
      ...historicalEmployeesList,
      ...scheduledMeetingsEmployeesList,
    ].filter((employee, index, self) => index === self.findIndex((e) => e.id === employee.id))

    const employees = uniqueEmployees
      .map((employee) => {
        const employeeScheduledMeetings =
          data?.meetings?.scheduledMeetingsConnection?.nodes?.filter((node) =>
            node.template?.templateAttendees?.some(
              (attendee) => attendee.employee?.id === employee.id
            )
          )

        const { future, past } = getNextClosestMeeting(employeeScheduledMeetings)

        const nextMeeting = (() => {
          if (future) {
            const isToday = future.time.isSame(moment().startOf('day'), 'day')
            return createMeetingInfo(
              future.meeting,
              future.time,
              locale,
              isToday ? 'info' : 'positive'
            )
          }
          if (past) {
            return createMeetingInfo(past.meeting, past.time, locale, 'neutral')
          }
          return null
        })()

        return {
          ...employee,
          directReport: directReportsList.some((e) => e.id === employee.id),
          isManager: !myReporteesList.includes(employee.id),
          isDirectManager: data?.currentEmployeeManager?.employees[0]?.manager?.id === employee.id,
          scheduledMeetings: employeeScheduledMeetings,
          nextMeeting,
        }
      })
      .filter((employee) => employee.id !== currentEmployee.id)

    return sortEmployeesByMeetingPriority(employees, locale)
  }, [currentEmployee.id, data, locale])

  const haveDirectReports = (data?.directReports?.employees?.length ?? 0) > 0
  const lastMeetingId = lastMeetingData?.meetings?.meetingsConnection?.nodes?.[0]?.id ?? undefined

  const toggleAiChat = () => {
    setAiChatOpen((prev) => {
      if (!prev) setTimeout(() => setShowAiChat(true), 500)
      else setShowAiChat(false)

      return !prev
    })
  }

  if (loadingEmployees || loadingLastMeeting) return <PageSkeleton />

  return (
    <Box flexDirection="column" height="full">
      {!canSeeAdvancedMeetings && (
        <Box paddingX="s16" paddingTop="s16">
          <UpsellingKit.ProductBanner
            title={i18n.t('meetings.one_on_ones.upsell.overview_banner.title')}
            subtitle={i18n.t('meetings.one_on_ones.upsell.overview_banner.subtitle')}
            mediaUrl={upsellOneOnOne}
            primaryAction={{
              label: i18n.t('meetings.one_on_ones.upsell.overview_banner.button'),
              variant: 'default',
            }}
            requestSource="one-on-ones-overview"
            requestReason={ValidReasons.MissingFeature}
            moduleRequested={AvailableModules.engagement}
            featureRequested={Features.engagement_one_on_one_meetings}
          />
        </Box>
      )}
      <Box flexDirection="row" height="full">
        <Box paddingTop="s16" width="full">
          {canSeeAdvancedMeetings && (
            <ConnectCalendarBanner
              accessCalendarSync={accessCalendarSync ?? null}
              loading={loading}
            />
          )}
          <TeamTable
            setOpenCreateScheduledMeeting={setOpenCreateScheduledMeeting}
            initialEmployees={initialEmployees}
            toggleAiChat={haveDirectReports && canSeeAdvancedMeetings ? toggleAiChat : undefined}
          />
        </Box>

        {haveDirectReports && (
          <Box
            height="full"
            overflowX="hidden"
            overflowY="hidden"
            style={{
              width: aiChatOpen ? '360px' : '0',
              transition: 'width 0.5s ease-in-out',
            }}
            border={{
              left: {
                width: 's1',
                style: 'solid',
                color: 'grey200',
              },
            }}
            flexNoShrink
          >
            {aiChatOpen && (
              <Box
                height="full"
                paddingBottom="s24"
                style={{
                  opacity: showAiChat ? '1' : '0',
                  transition: 'opacity 0.3s ease-in-out',
                }}
              >
                <AiChat
                  meetingId={lastMeetingId}
                  close={toggleAiChat}
                  blankStateType="manager_to_team"
                  isOverview
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {openCreateScheduledMeeting && (
        <CreateOneOnOneModal
          setOpenModal={setOpenCreateScheduledMeeting}
          canSeeAdvancedMeetings={canSeeAdvancedMeetings}
        />
      )}
    </Box>
  )
}

export default OneOnOnesOverviewPage
